import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import WorkIcon from '@mui/icons-material/Work';
import { Box } from '@mui/material';

import MicrositeImages from '~/components/atoms/microsite-images/MicrositeImages';
import { getVCardPlusReducer } from '~/store/reducers/previewScreens/vcard-plus/selectors';
import { COLORS } from '~/styles/colors';
import { mainPreviewContainerStyles } from '~/styles/common';
import { formatAddress } from '~/utils/common';
import { getIconPhoneByType } from '~/utils/getIconPhone';

import { downloadContacts } from '../service';
import { getIconByType, getPositionFlex } from '../utils';

import VCardPreviewAddContactButton from './VCardPreviewAddContactButton';
import VCardPreviewContactInfo from './VCardPreviewContactInfo';
import VCardPreviewFloatingAddContactBtn from './VCardPreviewFloatingAddContactBtn';
import VCardPreviewListItem from './VCardPreviewListItem';
import VCardPreviewLocation from './VCardPreviewLocation';
import VCardPreviewSocialBlock from './VCardPreviewSocialBlock';
import VCardPreviewSummary from './VCardPreviewSummary';
import VCardPreviewTitleAndBodyTexts from './VCardPreviewTitleAndBodyTexts';

type VCARD_PLUS_TEMPLATE_2Props = {
  isMyMicrosite?: boolean;
};
export function VCARD_PLUS_TEMPLATE_2({ isMyMicrosite }: VCARD_PLUS_TEMPLATE_2Props) {
  const vCardPlusState = useSelector(getVCardPlusReducer);
  const sectionFirst = useRef(null);
  const sectionSecond = useRef(null);
  const sectionThird = useRef(null);

  const scrollToSection = (item) => {
    switch (item) {
      case 'phone':
        return sectionFirst.current && sectionFirst.current.scrollIntoView({ behavior: 'smooth' });
      case 'email':
        return sectionSecond.current && sectionSecond.current.scrollIntoView({ behavior: 'smooth' });
      case 'geo':
        return sectionThird.current && sectionThird.current.scrollIntoView({ behavior: 'smooth' });
      default:
        return null;
    }
  };

  const {
    bacgkroundColor,
    fonts: { title, text },
    content,
    content: { socialTitle, footerTitle, footerType },
    micrositeStyles,
    micrositeId,
    micrositeData,
  } = vCardPlusState;

  const {
    images = [],
    imagesViewType,
    location = {},
    person: { firstName: name, lastName: surName, title: titleValue, photo: { logo: url } = {} } = {},
    personTitle: titlePosition,
    summary = '',
    phones = [],
    emails = [],
    websites = [],
    companies = [],
    socials = [],
  } = micrositeData || {};

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = summary;
  const textContent = tempDiv.textContent.trim();

  const [coordinates, setCoordinates] = useState(null);

  const isShowLocation =
    (location.street &&
      location.number &&
      location.postalCode &&
      location.city &&
      location.state &&
      location.country) ||
    (location.country && location.city) ||
    location.url ||
    (location.latitude && location.longitude);

  const addressString = formatAddress(location);

  const geocodeAddress = async (address) => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        setCoordinates({
          lat: location.lat(),
          lng: location.lng(),
        });
      }
    });
  };

  useEffect(() => {
    if (addressString) {
      const timer = setTimeout(() => {
        geocodeAddress(addressString);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [addressString]);

  const handleClickAddContact = () => {
    void downloadContacts(vCardPlusState?.micrositeData?.id || micrositeId);
  };

  return (
    <Box
      sx={[
        {
          ...mainPreviewContainerStyles,
          alignItems: 'normal',
          background: bacgkroundColor[0],
          ...micrositeStyles.container,
        },
        isMyMicrosite && {
          margin: 0,
          borderRadius: 0,
          minHeight: '100vh',
        },
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          minWidth: '100%',
          position: 'relative',
          paddingBottom: '12px',
        }}
      >
        {url && (
          <Box
            sx={{
              backgroundImage: `url(${url})`,
              width: '100%',
              height: 110,
              position: 'absolute',
              backgroundSize: '100% auto',
            }}
          >
            <Box sx={{ height: '100%', background: `linear-gradient(0deg, ${bacgkroundColor[0]}, transparent 90%)` }} />
          </Box>
        )}
        <Box sx={{ padding: '0 1rem' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', margin: '1rem 0' }}>
            <Box
              sx={{
                padding: 0.5,
                mt: 6.5,
                zIndex: 1,
              }}
            >
              <VCardPreviewTitleAndBodyTexts
                text={text}
                title={title}
                name={name}
                surName={surName}
                titlePosition={titlePosition}
                titleValue={titleValue}
              />
            </Box>
          </Box>

          <VCardPreviewContactInfo
            content={content}
            backgroundColor={bacgkroundColor[1]}
            scrollToSection={scrollToSection}
            containerJustifyContent={getPositionFlex(titlePosition)}
            isFullRounded
          />
          {footerTitle && footerType === 'top' && (
            <VCardPreviewAddContactButton
              label={footerTitle}
              backgroundColor={bacgkroundColor[1]}
              onClick={handleClickAddContact}
            />
          )}
          {!!textContent && <VCardPreviewSummary summary={summary} sx={{ margin: '8px 0' }} />}
          <Box ref={sectionFirst} sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
            {phones?.map((item) => {
              return (
                <VCardPreviewListItem
                  key={item.id}
                  Icon={getIconPhoneByType(item.type.toUpperCase(), 16)}
                  linkHref={`tel:${item.phone}`}
                  value={item.phone}
                  label={item.title}
                />
              );
            })}
          </Box>
          <Box ref={sectionSecond} sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
            {emails?.map((item) => {
              return (
                <VCardPreviewListItem
                  key={item.id}
                  Icon={getIconByType('email', COLORS.black06)}
                  linkHref={`mailto:${item.email}`}
                  value={item.email}
                  label={item.title}
                />
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
            {websites?.map((item) => {
              return (
                <VCardPreviewListItem
                  key={item.id}
                  Icon={getIconByType('website', COLORS.black06)}
                  linkHref={item.url}
                  value={item.url}
                  label={item.title}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              );
            })}
          </Box>
          {isShowLocation && (
            <VCardPreviewLocation
              addressString={addressString}
              coordinates={coordinates}
              location={location}
              ref={sectionThird}
            />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
            {companies.map((item) => {
              return (
                <VCardPreviewListItem
                  key={item.name}
                  Icon={<WorkIcon sx={{ color: COLORS.black06, width: '16px', height: '16px' }} />}
                  value={item.name}
                  label={item.profession}
                />
              );
            })}
          </Box>
          {!!socials.length && <VCardPreviewSocialBlock socialTitle={socialTitle} socialLinks={socials} />}

          {!!images.length && (
            <Box sx={{ padding: '8px 10px' }}>{<MicrositeImages data={images} type={imagesViewType} />}</Box>
          )}
          {footerTitle && footerType === null && (
            <VCardPreviewAddContactButton
              label={footerTitle}
              backgroundColor={bacgkroundColor[1]}
              onClick={handleClickAddContact}
            />
          )}
        </Box>
      </Box>
      {footerTitle && footerType === 'floating' && (
        <VCardPreviewFloatingAddContactBtn backgroundColor={bacgkroundColor[1]} onClick={handleClickAddContact} />
      )}
    </Box>
  );
}
